import {
  WorkingHoursApplyMode,
  TimeSlot,
  DaySnapshot,
  WorkingDay,
} from 'components/WorkingHours/WorkingHours.types';
import { UserSystemGroupStrings } from 'store/storeTypes';

export const WORKING_HOURS_SET_SITE = 'WORKING_HOURS/SET_SITE' as const;
export const WORKING_HOURS_SET_APPLY_MODE = 'WORKING_HOURS/SET_APPLY_MODE' as const;
export const WORKING_HOURS_SET_START_DATE = 'WORKING_HOURS/SET_START_DATE' as const;
export const WORKING_HOURS_TOGGLE_DAY = 'WORKING_HOURS/TOGGLE_DAY' as const;
export const WORKING_HOURS_ADD_SLOT = 'WORKING_HOURS/ADD_SLOT' as const;
export const WORKING_HOURS_ADD_SLOT_AT = 'WORKING_HOURS/ADD_SLOT_AT' as const;
export const WORKING_HOURS_UPDATE_SLOT = 'WORKING_HOURS/UPDATE_SLOT' as const;
export const WORKING_HOURS_REMOVE_SLOT = 'WORKING_HOURS/REMOVE_SLOT' as const;
export const WORKING_HOURS_SET_SYSTEM_GROUP = 'WORKING_HOURS/SET_SYSTEM_GROUP' as const;
export const WORKING_HOURS_SET_SYSTEM_GROUP_TYPES =
  'WORKING_HOURS/SET_SYSTEM_GROUP_TYPES' as const;
export const WORKING_HOURS_RESTORE_DAY = 'WORKING_HOURS/RESTORE_DAY' as const;
export const WORKING_HOURS_OPEN_EDITOR = 'WORKING_HOURS/OPEN_EDITOR' as const;
export const WORKING_HOURS_CLOSE_EDITOR = 'WORKING_HOURS/CLOSE_EDITOR' as const;
export const WORKING_HOURS_UPDATE_EDITOR_DRAFT = 'WORKING_HOURS/UPDATE_EDITOR_DRAFT' as const;
export const WORKING_HOURS_SET_EDITOR_APPLY_DAYS =
  'WORKING_HOURS/SET_EDITOR_APPLY_DAYS' as const;
export const WORKING_HOURS_APPLY_EDITOR = 'WORKING_HOURS/APPLY_EDITOR' as const;
export const WORKING_HOURS_SET_CONFIGURATION_FROM_API =
  'WORKING_HOURS/SET_CONFIGURATION_FROM_API' as const;
export const WORKING_HOURS_SET_VIEWING_SYSTEM_GROUP_TYPE =
  'WORKING_HOURS/SET_VIEWING_SYSTEM_GROUP_TYPE' as const;
export const WORKING_HOURS_SET_SITES = 'WORKING_HOURS/SET_SITES' as const;
export const WORKING_HOURS_SET_SITE_CONFIGURATION_FROM_API =
  'WORKING_HOURS/SET_SITE_CONFIGURATION_FROM_API' as const;

export const setWorkingHoursSite = (payload: string) => ({
  type: WORKING_HOURS_SET_SITE,
  payload,
});

export const setWorkingHoursSites = (payload: string[]) => ({
  type: WORKING_HOURS_SET_SITES,
  payload,
});

export const setWorkingHoursApplyMode = (payload: WorkingHoursApplyMode) => ({
  type: WORKING_HOURS_SET_APPLY_MODE,
  payload,
});

export const setWorkingHoursStartDate = (payload: string) => ({
  type: WORKING_HOURS_SET_START_DATE,
  payload,
});

export const toggleWorkingHoursDay = (payload: string) => ({
  type: WORKING_HOURS_TOGGLE_DAY,
  payload,
});

export const addWorkingHoursSlot = (payload: { dayId: string; slotId: string }) => ({
  type: WORKING_HOURS_ADD_SLOT,
  payload,
});

export const addWorkingHoursSlotAt = (payload: {
  dayId: string;
  slotId: string;
  startMinutes: number;
  durationMinutes: number;
}) => ({
  type: WORKING_HOURS_ADD_SLOT_AT,
  payload,
});

export const removeWorkingHoursSlot = (payload: { dayId: string; slotId: string }) => ({
  type: WORKING_HOURS_REMOVE_SLOT,
  payload,
});

export const updateWorkingHoursSlot = (payload: {
  dayId: string;
  slotId: string;
  startMinutes: number;
  endMinutes: number;
}) => ({
  type: WORKING_HOURS_UPDATE_SLOT,
  payload,
});

export const setWorkingHoursSystemGroup = (payload: UserSystemGroupStrings) => ({
  type: WORKING_HOURS_SET_SYSTEM_GROUP,
  payload,
});

export const setWorkingHoursSystemGroupTypes = (payload: UserSystemGroupStrings[]) => ({
  type: WORKING_HOURS_SET_SYSTEM_GROUP_TYPES,
  payload,
});

export const restoreWorkingHoursDay = (payload: { dayId: string } & DaySnapshot) => ({
  type: WORKING_HOURS_RESTORE_DAY,
  payload,
});

export const openWorkingHoursEditor = (payload: { dayId: string; slotId: string }) => ({
  type: WORKING_HOURS_OPEN_EDITOR,
  payload,
});

export const closeWorkingHoursEditor = () => ({
  type: WORKING_HOURS_CLOSE_EDITOR,
});

export const updateWorkingHoursEditorDraft = (payload: {
  startMinutes?: number;
  endMinutes?: number;
  overnight?: boolean;
}) => ({
  type: WORKING_HOURS_UPDATE_EDITOR_DRAFT,
  payload,
});

export const setWorkingHoursEditorApplyDays = (payload: { applyDays: string[] }) => ({
  type: WORKING_HOURS_SET_EDITOR_APPLY_DAYS,
  payload,
});

export const applyWorkingHoursEditor = () => ({
  type: WORKING_HOURS_APPLY_EDITOR,
});

export const setWorkingHoursConfigurationFromApi = (payload: {
  bySystemGroupType: Partial<Record<UserSystemGroupStrings, WorkingDay[]>>;
  isMixed: boolean;
}) => ({
  type: WORKING_HOURS_SET_CONFIGURATION_FROM_API,
  payload,
});

export const setWorkingHoursViewingSystemGroupType = (payload: UserSystemGroupStrings) => ({
  type: WORKING_HOURS_SET_VIEWING_SYSTEM_GROUP_TYPE,
  payload,
});

export const setWorkingHoursSiteConfigurationFromApi = (payload: {
  bySite: Partial<Record<string, WorkingDay[]>>;
  isMixed: boolean;
}) => ({
  type: WORKING_HOURS_SET_SITE_CONFIGURATION_FROM_API,
  payload,
});

export type WorkingHoursAction =
  | ReturnType<typeof setWorkingHoursSite>
  | ReturnType<typeof setWorkingHoursApplyMode>
  | ReturnType<typeof setWorkingHoursStartDate>
  | ReturnType<typeof toggleWorkingHoursDay>
  | ReturnType<typeof addWorkingHoursSlot>
  | ReturnType<typeof addWorkingHoursSlotAt>
  | ReturnType<typeof updateWorkingHoursSlot>
  | ReturnType<typeof removeWorkingHoursSlot>
  | ReturnType<typeof setWorkingHoursSystemGroup>
  | ReturnType<typeof setWorkingHoursSystemGroupTypes>
  | ReturnType<typeof restoreWorkingHoursDay>
  | ReturnType<typeof openWorkingHoursEditor>
  | ReturnType<typeof closeWorkingHoursEditor>
  | ReturnType<typeof updateWorkingHoursEditorDraft>
  | ReturnType<typeof setWorkingHoursEditorApplyDays>
  | ReturnType<typeof applyWorkingHoursEditor>
  | ReturnType<typeof setWorkingHoursConfigurationFromApi>
  | ReturnType<typeof setWorkingHoursViewingSystemGroupType>
  | ReturnType<typeof setWorkingHoursSites>
  | ReturnType<typeof setWorkingHoursSiteConfigurationFromApi>;
